import { useEffect, useState } from "react";
import { DollarSign, CalendarCheck, Users, TrendingUp } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { api, MXN } from "@/lib/api";

const STATUS_COLORS = {
  Confirmada: "#22C55E",
  Pendiente: "#F59E0B",
  Cancelada: "#EF4444",
  Finalizada: "#3B82F6",
};

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-border bg-card p-5 transition-transform hover:-translate-y-1">
      <div className="mb-3 grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary">
        <Icon size={18} strokeWidth={1.5} />
      </div>
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-muted-foreground">Cargando estadísticas...</p>;

  const statusData = Object.entries(data.status_counts).map(([name, value]) => ({ name, value }));

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <div>
        <h1 className="font-serif-display text-3xl">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Resumen de tu salón</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={DollarSign} label="Ingresos hoy" value={MXN(data.today_revenue)} testid="stat-today-revenue" />
        <Stat icon={TrendingUp} label="Ingresos del mes" value={MXN(data.month_revenue)} testid="stat-month-revenue" />
        <Stat icon={CalendarCheck} label="Citas hoy" value={data.today_count} testid="stat-today-count" />
        <Stat icon={Users} label="Clientes" value={data.clients_count} testid="stat-clients" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="border border-border bg-card p-6 lg:col-span-2">
          <h3 className="mb-4 font-serif-display text-xl">Ingresos últimos 7 días</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.revenue_by_day}>
              <XAxis dataKey="day" stroke="#888" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#888" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip
                formatter={(v) => MXN(v)}
                contentStyle={{ background: "#121212", border: "1px solid #27272A", color: "#fff", borderRadius: 0 }}
              />
              <Bar dataKey="revenue" fill="#D4AF37" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border bg-card p-6">
          <h3 className="mb-4 font-serif-display text-xl">Citas por estado</h3>
          {statusData.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin datos aún.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                  {statusData.map((s) => (
                    <Cell key={s.name} fill={STATUS_COLORS[s.name] || "#999"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #27272A", color: "#fff", borderRadius: 0 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="mt-4 space-y-1">
            {statusData.map((s) => (
              <div key={s.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLORS[s.name] }} />
                  {s.name}
                </span>
                <span className="text-muted-foreground">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-border bg-card p-6">
        <h3 className="mb-4 font-serif-display text-xl">Servicios más solicitados</h3>
        {data.top_services.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin datos aún.</p>
        ) : (
          <div className="space-y-3">
            {data.top_services.map((s, i) => {
              const max = data.top_services[0].count || 1;
              return (
                <div key={s.name}>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>{i + 1}. {s.name}</span>
                    <span className="text-muted-foreground">{s.count} citas</span>
                  </div>
                  <div className="h-2 bg-muted">
                    <div className="h-full bg-primary" style={{ width: `${(s.count / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
